import { Plus } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useCourierPickups } from '@/api/domains/courier-pickups';
import type { CourierPickup, CourierPickupFilters } from '@/api/types/pickups';
import { usePageTitle } from '@/components/layout/page-title';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

import { CancelPickupOrderDialog } from './CancelPickupOrderDialog';
import {
  CourierPickupsFilters,
  EMPTY_PICKUP_FILTERS,
  type CourierPickupFilterValues,
} from './CourierPickupsFilters';
import { CourierPickupsTable } from './CourierPickupsTable';
import { CreatePickupOrderDialog } from './CreatePickupOrderDialog';
import { PickupOrderDetailDialog } from './PickupOrderDetailDialog';

const PER_PAGE_OPTIONS = [5, 10, 25, 50];

function toApiFilters(
  values: CourierPickupFilterValues,
  page: number,
  perPage: number,
): CourierPickupFilters {
  return {
    pickup_location: values.pickup_location || null,
    // Each filter is a single date input; map it to an exact-day [from, to] range.
    date_created_from: values.date_created || null,
    date_created_to: values.date_created || null,
    date_collected_from: values.date_collected || null,
    date_collected_to: values.date_collected || null,
    status: values.status || null,
    page,
    per_page: perPage,
  };
}

export function CourierPickupsPage() {
  const { t } = useTranslation('pickup-orders');
  usePageTitle(t('title'));

  const [applied, setApplied] = useState<CourierPickupFilterValues>(EMPTY_PICKUP_FILTERS);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(5);
  const [createOpen, setCreateOpen] = useState(false);
  const [detailPickup, setDetailPickup] = useState<CourierPickup | null>(null);
  const [cancelPickup, setCancelPickup] = useState<CourierPickup | null>(null);

  const filters = useMemo(() => toApiFilters(applied, page, perPage), [applied, page, perPage]);
  const query = useCourierPickups(filters);

  const rows = query.data?.data ?? [];
  const meta = query.data?.meta;
  const hasFilters = Object.values(applied).some((value) => value !== '');

  return (
    <div className="space-y-9">
      <div className="flex flex-wrap items-center gap-4">
        <h2 className="text-4xl font-medium tracking-[-0.9px] text-black">{t('title')}</h2>
        <Button type="button" onClick={() => setCreateOpen(true)}>
          <Plus className="size-4" />
          {t('createOrder')}
        </Button>
      </div>

      <CourierPickupsFilters
        values={applied}
        onApply={(values) => {
          setApplied(values);
          setPage(1);
        }}
      />

      <div className="space-y-6">
        <CourierPickupsTable
          rows={rows}
          isLoading={query.isLoading}
          onOpen={setDetailPickup}
          emptyState={
            <div className="py-10 text-center text-sm text-muted-foreground">
              {hasFilters ? t('emptyFiltered') : t('empty')}
            </div>
          }
        />

        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="text-sm leading-5 text-black">{t('table.resultsPerPage')}</span>
            <Select
              value={String(perPage)}
              onValueChange={(v) => {
                setPerPage(Number(v));
                setPage(1);
              }}
            >
              <SelectTrigger className="h-9 w-16 rounded-[10px] border-[#e5e5e5] bg-white px-3 shadow-none focus:ring-0">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PER_PAGE_OPTIONS.map((option) => (
                  <SelectItem key={option} value={String(option)}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="flex h-9 items-center justify-center rounded-[10px] border border-[#e5e5e5] bg-white px-4 text-sm font-medium leading-5 text-foreground hover:bg-accent disabled:pointer-events-none disabled:opacity-50"
            >
              {t('table.previous')}
            </button>
            <button
              type="button"
              disabled={meta ? page >= meta.last_page : true}
              onClick={() => setPage((p) => p + 1)}
              className="flex h-9 items-center justify-center rounded-[10px] border border-[#e5e5e5] bg-white px-4 text-sm font-medium leading-5 text-foreground hover:bg-accent disabled:pointer-events-none disabled:opacity-50"
            >
              {t('table.next')}
            </button>
          </div>
        </div>
      </div>

      <CreatePickupOrderDialog open={createOpen} onOpenChange={setCreateOpen} />

      <PickupOrderDetailDialog
        initial={detailPickup}
        onOpenChange={(open) => !open && setDetailPickup(null)}
        onCancel={(pickup) => {
          setDetailPickup(null);
          setCancelPickup(pickup);
        }}
      />

      <CancelPickupOrderDialog
        pickup={cancelPickup}
        onOpenChange={(open) => !open && setCancelPickup(null)}
      />
    </div>
  );
}
